import * as React from 'react';
import { Modal, Button, FormGroup, FormControl, ControlLabel, HelpBlock } from 'react-bootstrap';
import { Translate } from '../../../shared/LanguageService';
import Map from '../../../features/map/Map';
import ICoordinate from '../../../features/map/ICoordinate';
import Marker from '../../../features/map//shapes/Marker';
import MapService from '../../../features/map/MapService';
import FormValidationService from '../../../shared/form/FormValidationService';

interface IProps {
    addressLine: string;
    postalNumber: string;
    city: string;
    point: ICoordinate;
    close: () => void;
    save: (position: ICoordinate) => void;
}

interface IState {
    address: string;
    point: ICoordinate;
    error: string;
}

class AddressMap extends React.Component<IProps, IState> {
    private currentmarker: Marker;

    constructor(props: IProps) {
        super(props);

        this.state = {
            address: '',
            point: null,
            error: null
        };

        this.search = this.search.bind(this);
        this.onMarkerChange = this.onMarkerChange.bind(this);
        this.onAddressChange = this.onAddressChange.bind(this);
        this.setAddressFromPosition = this.setAddressFromPosition.bind(this);
        this.onAddressKeyPress = this.onAddressKeyPress.bind(this);
        this.save = this.save.bind(this);
        this.getAddress = this.getAddress.bind(this);
        this.getCurrentLocationCoordinate = this.getCurrentLocationCoordinate.bind(this);
    }

    componentDidMount() {
        if (this.props.point) {
            this.currentmarker = new Marker(this.props.point, true, true, this.onMarkerChange);
            this.setAddressFromPosition(this.props.point);
            this.setState({ point: this.props.point } as IState);
        } else {
            let address = [];
            if (this.props.addressLine) {
                address.push(this.props.addressLine);
            }

            if (this.props.postalNumber) {
                address.push(this.props.postalNumber);
            }

            if (this.props.city) {
                address.push(this.props.city);
            }

            if (address.length > 0) {
                let addressString = address.join(', ');
                this.setState({ address: addressString } as IState);
                this.getAddress(addressString).then((result: google.maps.GeocoderResult) => {
                    if (!result) {
                        this.getCurrentLocationCoordinate();
                    } else {
                        let coordinate: ICoordinate = {
                            longitude: result.geometry.location.lng(),
                            latitude: result.geometry.location.lat()
                        };

                        this.setState({
                            point: coordinate,
                            error: null
                        } as IState);
                    }
                });
            } else {
                this.getCurrentLocationCoordinate();
            }
        }
    }

    onAddressChange(e: any) {
        this.setState({ address: e.target.value } as IState);
    }

    search() {
        this.getAddress(this.state.address);
    }

    getAddress(address: string): Promise<google.maps.GeocoderResult> {
        return MapService.getAddress(address).then((result: google.maps.GeocoderResult) => {
            if (result) {
                let coordinate: ICoordinate = {
                    longitude: result.geometry.location.lng(),
                    latitude: result.geometry.location.lat()
                };

                if (!this.currentmarker) {
                    this.currentmarker = new Marker(coordinate, true, true, this.onMarkerChange);
                } else {
                    this.currentmarker.setPosition(coordinate);
                }

                this.setState({
                    address: result.formatted_address,
                    point: coordinate,
                    error: null
                } as IState);

                return result;
            } else {
                this.setState({
                    error: Translate('address-validation/form/error/address-not-found')
                } as IState);
                return result;
            }
        });
    }

    getCurrentLocationCoordinate() {
        MapService.getCurrentLocationCoordinate().then((coordinate: ICoordinate) => {
            this.currentmarker = new Marker(coordinate, true, true, this.onMarkerChange);
            this.setAddressFromPosition(coordinate);
            this.setState({ point: coordinate, error: null } as IState);
        });
    }

    onMarkerChange(position: ICoordinate) {
        this.setAddressFromPosition(position);
    }

    setAddressFromPosition(position: ICoordinate) {
        MapService.getAddresFromPosition(position.longitude, position.latitude).then(
            (result: google.maps.GeocoderResult) => {
                if (result) {
                    this.setState({ address: result.formatted_address } as IState);
                }
            }
        );
    }

    onAddressKeyPress(e: any) {
        // If enter is pressed
        if (e.charCode === 13) {
            this.search();
        }
    }

    save() {
        this.props.save(this.currentmarker.Center);
    }

    render() {
        return (
            <Modal bsSize="large" show={true} onHide={this.props.close}>
                <Modal.Header closeButton>
                    <Modal.Title>{Translate('address-validation/form/title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="image-modal">
                    <div>
                        <FormGroup
                            validationState={FormValidationService.getValidationState(
                                this.state.error
                            )}
                        >
                            <ControlLabel>
                                {Translate('address-validation/form/address-line')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="address"
                                value={this.state.address ? this.state.address : ''}
                                onChange={this.onAddressChange}
                                onKeyPress={this.onAddressKeyPress}
                            />
                            {this.state.error && <HelpBlock>{this.state.error}</HelpBlock>}
                        </FormGroup>

                        <Button onClick={this.search}>
                            {Translate('address-validation/form/button/search')}
                        </Button>
                    </div>
                    <div>
                        {this.state.point && (
                            <Map
                                settings={[{ marker: this.currentmarker }]}
                                dontReRender={true}
                                zoom={15}
                                center={this.state.point}
                                mapId="address-validation-map"
                            />
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.props.close}>
                        {Translate('address-validation/form/button/close')}
                    </Button>
                    <Button bsStyle="success" onClick={this.save}>
                        {Translate('address-validation/form/button/save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default AddressMap;
